import React, {useEffect,useMemo,useState,} from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { buildFinalReport } from "./utils/buildFinalReport";
import { sampleFinalReport } from "./data/sampleFinalReport";
import jsPDF from "jspdf";


const Bar = ({value = 0,color = "bg-cyan-400",}) => {
    return (
        <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">
            <div
                className={`h-full rounded-full transition-all duration-700 ${color}`}
                style={{
                    width: `${Math.min(
                        100,
                        Math.max(value, 0)
                    )}%`,
                }}
            />
        </div>
    );
};

const TrendBar = ({
    label,
    value,
}) => {
    return (
        <div className="flex flex-col items-center flex-1">
            <div className="text-[10px] text-gray-500 mb-2">
                {label}
            </div>

            <div className="w-full h-24 bg-[#111827] rounded-xl flex items-end p-1">
                <div
                    className="w-full bg-cyan-400 rounded-lg transition-all duration-700"
                    style={{
                        height: `${Math.max(
                            value,
                            6
                        )}%`,
                    }}
                />
            </div>

            <div className="text-[10px] text-cyan-300 mt-2 font-bold">
                {value}%
            </div>
        </div>
    );
};

const AchievementCard = ({
    emoji,
    title,
    desc,
}) => {
    return (
        <div className="bg-[#111827] rounded-xl p-3 border border-gray-800">
            <div className="text-lg">
                {emoji}
            </div>

            <div className="text-xs font-bold mt-1">
                {title}
            </div>

            <div className="text-[10px] text-gray-400 mt-1 leading-5">
                {desc}
            </div>
        </div>
    );
};

const PDReport = ({useSample, isActiveSub, startDate, endDate}) => {

    const authState = useSelector(
        (state) => state.authReducer
    );

    const [fetchData] = useFetch();

    const userId =
        authState?.user?._id;

    const [loading, setLoading] =
        useState(false);

    const [reportData, setReportData] =
        useState(null);

    // FETCH REPORT
    const fetchFinalReport = async () => {

        try {

            setLoading(true);

            const config = {
                url: "/pd/my/todoroaches/raw",
                method: "post",
                data: {
                    userId,
                    startDate: startDate,
                    endDate: endDate,
                },
                headers: {
                    Authorization:
                        authState.token,
                },
            };

            const response = await fetchData( config, { silent: true });

// console.log("FULsL REPORT RESPONSE:", response);
            if (response?.status) {
                const rawTodos = response?.todos || [];
                const rawRoach = response?.roachEntries || [];

                const report = buildFinalReport({
                    todos: rawTodos,
                    roachEntries: rawRoach,
                });

                setReportData(report);
            }

        } catch (error) {

            console.error(
                "Final Report Error:",
                error
            );

        } finally {

            setLoading(false);
        }
    };

    useEffect(() => {

        if (useSample) return;

        if (userId) {
            fetchFinalReport();
        }
    }, [userId, useSample]);

    const d = useMemo(() => {
        if (useSample) {
            return sampleFinalReport;
        }
        return (
            reportData || {
                overview: {},
                stats: {},
                signals: {},
                behavior: {},
                objectives: {},
                strengths: [],
                weaknesses: [],
                insights: [],
                comparison: {},
                momentum: {},
                journey: {},
            }
        );
    }, [reportData, useSample]);

    const overallScore =
        d?.overview
            ?.productivityScore || 0;

    const improvement =
        d?.overview
            ?.productivityGrowth || 0;

    const performerLabel =
        overallScore >= 80
            ? "Elite Performer"
            : overallScore >= 60
            ? "Consistent Builder"
            : "Needs Momentum";

    const completionRate =
        d?.stats?.total
            ? Math.round(
                  (
                      (d?.stats?.done || 0) /
                      d?.stats?.total
                  ) * 100
              )
            : 0;

    const achievements = [];

    if (
        d?.signals?.consistency >= 75
    ) {
        achievements.push({
            emoji: "🔥",
            title:
                "Consistency Builder",
            desc:
                "You maintained strong execution discipline across the challenge.",
        });
    }

    if (
        d?.signals?.focus >= 60
    ) {
        achievements.push({
            emoji: "🎯",
            title: "Focus Mode",
            desc:
                "Your deep-work orientation improved significantly.",
        });
    }

    if (
        d?.signals?.resilience >= 60
    ) {
        achievements.push({
            emoji: "🪖",
            title: "Resilient Survivor",
            desc:
                "Your behavioral resilience stayed stable under pressure.",
        });
    }

    if (
        improvement >= 15
    ) {
        achievements.push({
            emoji: "🚀",
            title:
                "Growth Transformation",
            desc:
                "You showed measurable productivity improvement over time.",
        });
    }

    const handleShareFinalReport = () => {

        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        let y = 20;

        const addSection = (title) => {

            if (y > pageHeight - 25) {
                pdf.addPage();
                y = 20;
            }

            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(14);
            pdf.text(title, 15, y);
            y += 8;
        };

        const addText = (text) => {

            if (!text) return;

            const lines = pdf.splitTextToSize(
                String(text),
                180
            );

            if (y + lines.length * 5 > pageHeight - 20) {
                pdf.addPage();
                y = 20;
            }

            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(10);
            pdf.text(lines, 15, y);

            y += lines.length * 5 + 3;
        };

        // ==========================
        // COVER
        // ==========================

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(22);

        pdf.text(
            "PurpleDoor Final 90-Day Report",
            pageWidth / 2,
            25,
            { align: "center" }
        );

        pdf.setFontSize(11);

        pdf.text(
            `Generated: ${new Date().toLocaleString()}`,
            pageWidth / 2,
            35,
            { align: "center" }
        );

        y += 20;

        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Name: ${authState?.user?.name}`, 15, y);

        y += 10;

        addSection("Executive Summary");

        addText(`Final Grade: ${d?.overview?.finalGrade || "C"}`);
        addText(`Final Productivity Score: ${overallScore}/100`);
        addText(`Performance Category: ${performerLabel}`);
        addText(
            `Growth: ${
                improvement >= 0
                    ? "+" + improvement
                    : improvement
            }%`
        );

        // ==========================
        // JOURNEY METRICS
        // ==========================

        addSection("Journey Snapshot");

        addText(
            `Total Tasks Completed During Journey: ${
                d?.stats?.total || 0
            }`
        );

        addText(
            `Completion Rate: ${completionRate}%`
        );

        addText(
            `Survivor Type: ${
                d?.behavior?.roachType ||
                "Unknown"
            }`
        );

        // ==========================
        // PROGRESSION
        // ==========================

        addSection("90-Day Progression");

        addText(
            `Day 0 Productivity: ${
                d?.comparison?.start
                    ?.productivity || 0
            }`
        );

        addText(
            `Mid Journey Productivity: ${
                d?.comparison?.middle
                    ?.productivity || 0
            }`
        );

        addText(
            `Final Productivity: ${
                d?.comparison?.end
                    ?.productivity || 0
            }`
        );

        // ==========================
        // SIGNALS
        // ==========================

        addSection("Behavioral Signal Analysis");

        addText(
            `Consistency: ${
                d?.signals?.consistency || 0
            }%`
        );

        addText(
            `Focus Strength: ${
                d?.signals?.focus || 0
            }%`
        );

        addText(
            `Discipline: ${
                d?.signals?.discipline || 0
            }%`
        );

        addText(
            `Resilience: ${
                d?.signals?.resilience || 0
            }%`
        );

        // ==========================
        // ACHIEVEMENTS
        // ==========================

        addSection("Achievements Unlocked");

        if (achievements?.length) {

            achievements.forEach((a) => {

                addText(
                    `${a.emoji} ${a.title} - ${a.desc}`
                );

            });

        } else {

            addText(
                "No achievements unlocked during this cycle."
            );

        }

        // ==========================
        // OBJECTIVES
        // ==========================

        addSection("Objective Personality");

        addText(
            `Dominant Objective: ${
                d?.objectives?.dominant ||
                "Unknown"
            }`
        );

        Object.entries(
            d?.objectives?.distribution || {}
        ).forEach(([key, value]) => {

            addText(
                `${key}: ${value} activities`
            );

        });

        // ==========================
        // STRENGTHS
        // ==========================

        addSection("Strongest Improvements");

        (d?.strengths || []).forEach(
            (item) => addText(`• ${item}`)
        );

        // ==========================
        // WEAKNESSES
        // ==========================

        addSection(
            "Improvement Opportunities"
        );

        (d?.weaknesses || []).forEach(
            (item) => addText(`• ${item}`)
        );

        // ==========================
        // INSIGHTS
        // ==========================

        addSection(
            "System Outcome Summary"
        );

        (d?.insights || []).forEach(
            (item) => addText(`• ${item}`)
        );

        // ==========================
        // FINAL MESSAGE
        // ==========================

        addSection(
            "Your Next Evolution Starts Now"
        );

        addText(
            "You completed one transformation cycle. The next 90 days can push your focus, discipline and execution to an entirely new level."
        );

        addText(
            "Generated by Think5Whys PurpleDoor Behavioural Operating System."
        );

        pdf.save(
            `PurpleDoor-Final-90-Day-Report-${new Date()
                .toISOString()
                .split("T")[0]}.pdf`
        );
    };


    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-blue-400 mt-2">
            {useSample && (
                <div className="space-y-6 bg-gray-700 p-6 rounded-xl text-white border-t-2 border-blue-600 mt-2">
                    📦 You are viewing SAMPLE DASHBOARD DATA (for demo purpose)
                </div>
            )}
            {/* LOADING */}
            {loading && (
                <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800 text-sm text-gray-400">
                    Loading final report...
                </div>
            )}

            {/* HEADER */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="flex items-start justify-between">

                    <div>

                        <div className="text-lg font-bold">
                            🏆 Final 90-Day Report
                        </div>

                        <div className="text-xs text-gray-400 mt-1">
                            Your transformation journey from Day 0 → Day 90
                        </div>

                    </div>

                    <div className="bg-purple-500/20 text-purple-300 px-3 py-2 rounded-xl text-sm font-bold">
                        GRADE {d?.overview?.finalGrade || "C"}
                    </div>

                </div>

                <div className="mt-5">

                    <div className="text-5xl font-black text-white">
                        {overallScore}
                        <span className="text-xl text-gray-400">
                            /100
                        </span>
                    </div>

                    <div className="text-xs text-gray-400 mt-2">
                        Final Productivity Score
                    </div>

                    <div className="mt-4">
                        <Bar
                            value={overallScore}
                            color="bg-purple-400"
                        />
                    </div>

                    <div className="flex items-center justify-between mt-3">

                        <div className="text-xs text-green-400 font-bold">
                            🚀
                            {improvement >= 0
                                ? ` +${improvement}% Growth`
                                : ` ${improvement}% Decline`}
                        </div>

                        <div className="text-xs text-cyan-300 font-bold">
                            {performerLabel}
                        </div>

                    </div>

                </div>

            </div>

            {/* JOURNEY SNAPSHOT */}
            <div className="grid grid-cols-3 gap-2">

                <div className="bg-[#1f2937] p-3 rounded-2xl border border-gray-800 text-center">

                    <div className="text-[10px] text-gray-400">
                        📊 Tasks
                    </div>

                    <div className="text-xl font-black text-white mt-1">
                        {d?.stats?.total || 0}
                    </div>

                </div>

                <div className="bg-[#1f2937] p-3 rounded-2xl border border-gray-800 text-center">

                    <div className="text-[10px] text-gray-400">
                        ✅ Completion
                    </div>

                    <div className="text-xl font-black text-green-400 mt-1">
                        {completionRate}%
                    </div>

                </div>

                <div className="bg-[#1f2937] p-3 rounded-2xl border border-gray-800 text-center">

                    <div className="text-[10px] text-gray-400">
                        🪖 Survivor Type
                    </div>

                    <div className="text-[11px] font-bold text-cyan-400 mt-2 leading-4">
                        {d?.behavior?.roachType ||
                            "Unknown"}
                    </div>

                </div>

            </div>

            {/* DAY 0 VS DAY 90 */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">

                <div className="flex items-center justify-between">

                    <div className="text-sm font-bold">
                        📈 Journey Progression
                    </div>

                    <div className="text-[10px] text-gray-400">
                        Day 0 → Day 90
                    </div>

                </div>

                <div className="grid grid-cols-3 gap-3 mt-5">

                    <TrendBar
                        label="START"
                        value={
                            d?.comparison
                                ?.start
                                ?.productivity || 0
                        }
                    />

                    <TrendBar
                        label="MIDDLE"
                        value={
                            d?.comparison
                                ?.middle
                                ?.productivity || 0
                        }
                    />

                    <TrendBar
                        label="FINAL"
                        value={
                            d?.comparison
                                ?.end
                                ?.productivity || 0
                        }
                    />

                </div>

            </div>

            {/* PERFORMANCE BREAKDOWN */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800 space-y-4">

                <div className="flex items-center justify-between">

                    <div className="text-sm font-bold">
                        📊 Final Signal Analysis
                    </div>

                    <div className="text-[10px] text-gray-400">
                        Behavioral Metrics
                    </div>

                </div>

                {/* CONSISTENCY */}
                <div>

                    <div className="flex items-center justify-between mb-1">

                        <div className="text-xs text-gray-300">
                            🔁 Consistency
                        </div>

                        <div className="text-xs text-cyan-300 font-bold">
                            {d?.signals?.consistency || 0}%
                        </div>

                    </div>

                    <Bar
                        value={
                            d?.signals
                                ?.consistency || 0
                        }
                    />

                </div>

                {/* FOCUS */}
                <div>

                    <div className="flex items-center justify-between mb-1">

                        <div className="text-xs text-gray-300">
                            🎯 Focus Strength
                        </div>

                        <div className="text-xs text-green-300 font-bold">
                            {d?.signals?.focus || 0}%
                        </div>

                    </div>
                    <Bar
                        value={
                            d?.signals?.focus || 0
                        }
                        color="bg-green-400"
                    />
                </div>

                {/* DISCIPLINE */}
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-300">
                            🧠 Discipline
                        </div>
                        <div className="text-xs text-yellow-300 font-bold">
                            {d?.signals?.discipline || 0}%
                        </div>
                    </div>
                    <Bar
                        value={
                            d?.signals
                                ?.discipline || 0
                        }
                        color="bg-yellow-400"
                    />
                </div>

                {/* RESILIENCE */}
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-300">
                            🪖 Resilience
                        </div>

                        <div className="text-xs text-purple-300 font-bold">
                            {d?.signals?.resilience || 0}%
                        </div>
                    </div>
                    <Bar
                        value={
                            d?.signals
                                ?.resilience || 0
                        }
                        color="bg-purple-400"
                    />
                </div>
            </div>

            {/* ACHIEVEMENTS */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="text-sm font-bold mb-3">
                    🏅 Achievement Unlocks
                </div>

                <div className="grid grid-cols-2 gap-2">
                    {achievements.length ? (
                        achievements.map(
                            (
                                achievement,
                                index
                            ) => (
                                <AchievementCard
                                    key={index}
                                    emoji={
                                        achievement.emoji
                                    }
                                    title={
                                        achievement.title
                                    }
                                    desc={
                                        achievement.desc
                                    }
                                />
                            )
                        )
                    ) : (
                        <div className="col-span-2 bg-[#111827] rounded-xl p-4 text-xs text-gray-400">
                            Keep building consistency to unlock achievements.
                        </div>
                    )}
                </div>
            </div>

            {/* OBJECTIVE PROFILE */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="flex items-center justify-between">
                    <div className="text-sm font-bold">
                        🎯 Objective Personality
                    </div>
                    <div className="text-[10px] text-cyan-300 font-bold">
                        {
                            d?.objectives
                                ?.dominant
                        }
                    </div>
                </div>

                <div className="mt-4 space-y-3">

                    {Object.entries(
                        d?.objectives
                            ?.distribution || {}
                    ).map(
                        (
                            [key, value],
                            index
                        ) => {

                            const total =
                                d?.stats
                                    ?.total || 1;

                            const percent =
                                Math.round(
                                    (
                                        value /
                                        total
                                    ) * 100
                                );

                            return (
                                <div
                                    key={index}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="text-xs text-gray-300">
                                            {key}
                                        </div>
                                        <div className="text-xs text-cyan-300 font-bold">
                                            {percent}%
                                        </div>

                                    </div>
                                    <Bar
                                        value={
                                            percent
                                        }
                                    />
                                </div>
                            );
                        }
                    )}
                </div>
            </div>

            {/* STRENGTHS */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="text-sm font-bold mb-3 text-green-400">
                    ✅ Your Strongest Improvements
                </div>
                <div className="space-y-2">
                    {(d?.strengths || []).map(
                        (
                            item,
                            index
                        ) => (
                            <div
                                key={index}
                                className="bg-[#111827] rounded-xl p-3"
                            >
                                <div className="text-xs text-gray-300 leading-5">
                                    {item}
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* WEAKNESSES */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="text-sm font-bold mb-3 text-yellow-400">
                    ⚠️ Improvement Opportunities
                </div>
                <div className="space-y-2">
                    {(d?.weaknesses || []).map(
                        (
                            item,
                            index
                        ) => (
                            <div
                                key={index}
                                className="bg-[#111827] rounded-xl p-3"
                            >
                                <div className="text-xs text-gray-300 leading-5">
                                    {item}
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* SYSTEM INSIGHTS */}
            <div className="bg-[#111827] rounded-2xl p-4 border border-cyan-500/20">
                <div className="text-sm font-bold text-cyan-400">
                    📌 System Outcome Summary
                </div>
                <div className="space-y-3 mt-4">
                    {(d?.insights || []).map(
                        (
                            insight,
                            index
                        ) => (
                            <div
                                key={index}
                                className="bg-[#1f2937] rounded-xl p-3"
                            >
                                <div className="text-xs text-gray-300 leading-6">
                                    {insight}
                                </div>
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* FINAL CTA */}
            <div className="bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/20 rounded-2xl p-4">
                <div className="text-sm font-bold">
                    🚀 Your Next Evolution Starts Now
                </div>
                <div className="text-xs text-gray-300 mt-2 leading-5">
                    You completed one transformation cycle. The next 90 days can push your focus,
                    discipline, and execution to a completely new level.
                </div>

                <div className="flex flex-tow gap-2">
                    <button className="w-48 mt-4 bg-cyan-500 hover:bg-cyan-400 text-black py-1 rounded-xl text-xs transition-all">
                        🎯 Start Next 90-Day Cycle
                    </button>
                    <button
                        onClick={handleShareFinalReport}
                        className="w-48 bg-green-600 hover:bg-green-500 transition-all py-1 rounded-xl text-sm"
                    >
                        📄 Share Final Report
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PDReport;